import type { ShareType } from '../components/MessageShareButton/share-target';

/** Names of the Sidekick feature events a host can track. */
export const SIDEKICK_FEATURE_EVENT = {
  MESSAGE_COPIED: 'messageCopied',
  RESPONSE_SHARED: 'responseShared',
  RESPONSE_STOPPED: 'responseStopped',
  QUERY_EDITED: 'queryEdited',
  BRANCH_SWITCHED: 'branchSwitched',
  CONVERSATION_RESET: 'conversationReset',
  SUGGESTION_SELECTED: 'suggestionSelected',
  UPSELL_VIEWED: 'upsellViewed',
  EXPLORE_PLANS_CLICKED: 'explorePlansClicked',
  CONSENT_ACCEPTED: 'consentAccepted',
} as const;

/** Which side of a turn was copied. */
export type SidekickCopyTarget = 'query' | 'response';

/** Where a suggestion chip was picked. */
export type SidekickSuggestionSurface = 'dock' | 'panel';

/** Which neighboring branch a switch moved to. */
export type SidekickBranchDirection = 'previous' | 'next';

/** Where a picked chip sat among the chips on offer. */
export type SidekickSuggestionPlacement = {
  /** One-based position of the chip. */
  chipPosition: number;
  /** How many chips were on offer. */
  chipCount: number;
};

/**
 * What the user did. Carries no question, answer, or chip text, so
 * hosts cannot forward what the user asked to their analytics.
 */
type SidekickFeatureEventDetail =
  | {
      type: typeof SIDEKICK_FEATURE_EVENT.MESSAGE_COPIED;
      target: SidekickCopyTarget;
      /** Backend trace of a copied answer, when it has one. */
      traceId?: string;
    }
  | {
      type: typeof SIDEKICK_FEATURE_EVENT.RESPONSE_SHARED;
      /** Backend trace of the shared answer, when it has one. */
      traceId?: string;
      shareType: ShareType;
      recipientCount: number;
    }
  | { type: typeof SIDEKICK_FEATURE_EVENT.RESPONSE_STOPPED }
  | {
      type: typeof SIDEKICK_FEATURE_EVENT.QUERY_EDITED;
      /** Character count of the edited question. */
      inputLength: number;
    }
  | {
      type: typeof SIDEKICK_FEATURE_EVENT.BRANCH_SWITCHED;
      direction: SidekickBranchDirection;
      /** How many branches the edited question has. */
      branchCount: number;
    }
  | {
      type: typeof SIDEKICK_FEATURE_EVENT.CONVERSATION_RESET;
      /** Messages the conversation held when it was reset. */
      messageCount: number;
    }
  | ({
      type: typeof SIDEKICK_FEATURE_EVENT.SUGGESTION_SELECTED;
      surface: SidekickSuggestionSurface;
    } & SidekickSuggestionPlacement)
  | {
      type: typeof SIDEKICK_FEATURE_EVENT.UPSELL_VIEWED;
      /** Whether the user was offered a way into the plan catalog. */
      hasExplorePlansCta: boolean;
    }
  | { type: typeof SIDEKICK_FEATURE_EVENT.EXPLORE_PLANS_CLICKED }
  | { type: typeof SIDEKICK_FEATURE_EVENT.CONSENT_ACCEPTED };

/**
 * A use of a Sidekick feature, reported once the action has taken
 * effect: a copy that reached the clipboard, a share the backend
 * accepted, a chip whose question was sent.
 */
export type SidekickFeatureEvent = SidekickFeatureEventDetail & {
  /**
   * The conversation the action happened in, or `null` before the
   * first question.
   */
  conversationId: string | null;
};

/**
 * A feature event as package components report it. Without a
 * conversation id, the active conversation's is used.
 */
export type SidekickFeatureEventInput = SidekickFeatureEventDetail & {
  conversationId?: string;
};

/** Host callback that receives each Sidekick feature event. */
export type SidekickFeatureEventCallback = (
  event: SidekickFeatureEvent
) => void;
